/**
 * Builds an API filter object using field-level transform functions.
 * @param {object} filters - Source UI filters.
 * @param {object<string, function>} fieldTransforms - Map of API field to transform callback.
 * @returns {object}
 */
export const buildApiFilters = (filters, fieldTransforms) => {
    const apiFilters = {}

    for (const [apiField, transform] of Object.entries(fieldTransforms)) {
        apiFilters[apiField] = transform(filters)
    }

    return apiFilters
}

/**
 * Converts a UI "any" selection to empty string for API requests.
 * @param {string} value
 * @param {string} anyValue
 * @returns {string}
 */
export const mapAnyValueToEmpty = (value, anyValue) =>
    value === anyValue ? '' : value

/**
 * Builds a URL query string from a plain object.
 * Skips null, undefined, and empty-string values.
 * @param {object} params
 * @returns {string}
 */
const isEmptyQueryValue = (value) => value == null || value === ''

export const buildQueryString = (params) => {
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
        if (isEmptyQueryValue(value)) {
            return
        }

        if (Array.isArray(value)) {
            value.forEach((arrayValue) => {
                if (!isEmptyQueryValue(arrayValue)) {
                    queryParams.append(key, String(arrayValue))
                }
            })
            return
        }

        queryParams.append(key, String(value))
    })

    const encodedQuery = queryParams.toString()
    return encodedQuery ? `?${encodedQuery}` : ''
}

/**
 * Computes the next tri-state sort settings for a clicked column.
 * Cycle: unsorted/new column -> asc (1) -> desc (-1) -> unsorted.
 * @param {{
 *  currentSortBy: string|null,
 *  currentSortDirection: number,
 *  column: string
 * }} params
 * @returns {{nextSortBy: string|null, nextSortDirection: number}}
 */
export const getNextSortState = ({
    currentSortBy,
    currentSortDirection,
    column,
}) => {
    if (currentSortBy !== column) {
        return {
            nextSortBy: column,
            nextSortDirection: 1,
        }
    }

    if (currentSortDirection === 1) {
        return {
            nextSortBy: column,
            nextSortDirection: -1,
        }
    }

    return {
        nextSortBy: null,
        nextSortDirection: 1,
    }
}
